/**
 * The rules of `amendPlan` (contracts-v3) in one place, so the mock and the real coordinator apply them the same way.
 * Pure: it returns the amended plan and the packages that changed; the caller stores them and emits the events.
 */
import { CoordinatorError, type AmendPlanInput } from "./coordinator.ts";
import { WorkPlanSchema, type PlanAmendment, type WorkPackage, type WorkPlan } from "./plan.ts";
import { isSystemPrincipal } from "./primitives.ts";

/** What {@link amendPlanRecord} produced. */
export interface AmendedPlan {
  plan: WorkPlan;
  amendment: PlanAmendment;
  /** Every package that was added or changed, in plan order. */
  changed: WorkPackage[];
}

const FINAL = new Set(["landed", "superseded"]);

/**
 * Apply an amendment to a copy of `plan`. `ownerOf(wpId)` names the plan a package id already belongs to, if any.
 * Throws a {@link CoordinatorError} when the amendment is not allowed.
 */
export function amendPlanRecord(plan: WorkPlan, input: AmendPlanInput, ts: string, ownerOf: (wpId: string) => string | undefined): AmendedPlan {
  if (!plan.approvedBy) throw new CoordinatorError("precondition_failed", `plan ${plan.specId} is not approved; resubmit the draft instead`);
  if (!isSystemPrincipal(input.amendedBy)) throw new CoordinatorError("forbidden", "only a system: principal (the run's commander) can amend a plan");
  const next: WorkPlan = structuredClone(plan);
  const byId = new Map(next.packages.map((p) => [p.id, p]));
  const changed = new Set<string>();

  const added = input.add ?? [];
  for (const d of added) {
    if (byId.has(d.id) || (ownerOf(d.id) && ownerOf(d.id) !== plan.specId)) throw new CoordinatorError("conflict", `${d.id} already exists`);
    const pkg: WorkPackage = { ...structuredClone(d), status: "approved", forkRepo: null, sessionId: null };
    next.packages.push(pkg);
    byId.set(pkg.id, pkg);
    changed.add(pkg.id);
  }
  if (new Set(added.map((d) => d.id)).size !== added.length) throw new CoordinatorError("invalid_input", "added package ids must be unique");

  const superseded = input.supersede ?? [];
  for (const s of superseded) {
    const pkg = byId.get(s.wpId);
    if (!pkg) throw new CoordinatorError("not_found", `work package ${s.wpId} is not in plan ${plan.specId}`);
    if (FINAL.has(pkg.status)) throw new CoordinatorError("conflict", `${s.wpId} is already ${pkg.status}`);
    for (const r of s.replacedBy) {
      const rep = byId.get(r);
      if (!rep || r === s.wpId || rep.status === "superseded") throw new CoordinatorError("invalid_input", `${s.wpId} cannot be replaced by ${r}`);
    }
  }
  for (const s of superseded) {
    byId.get(s.wpId)!.status = "superseded";
    changed.add(s.wpId);
  }
  // Dependents of a superseded package wait on its replacements instead.
  const replacements = new Map(superseded.map((s) => [s.wpId, s.replacedBy]));
  for (const p of next.packages) {
    if (FINAL.has(p.status) || !p.dependsOn.some((d) => replacements.has(d))) continue;
    p.dependsOn = [...new Set(p.dependsOn.flatMap((d) => replacements.get(d) ?? [d]))].filter((d) => d !== p.id);
    changed.add(p.id);
  }

  const rewired = input.rewire ?? [];
  for (const r of rewired) {
    const pkg = byId.get(r.wpId);
    if (!pkg) throw new CoordinatorError("not_found", `work package ${r.wpId} is not in plan ${plan.specId}`);
    if (FINAL.has(pkg.status)) throw new CoordinatorError("conflict", `${r.wpId} is already ${pkg.status}`);
    for (const d of r.addDependsOn) {
      if (!byId.has(d) || d === r.wpId) throw new CoordinatorError("invalid_input", `${r.wpId} cannot depend on ${d}`);
      if (byId.get(d)!.status === "superseded") throw new CoordinatorError("invalid_input", `${r.wpId} cannot depend on superseded ${d}`);
    }
    pkg.dependsOn = [...new Set([...pkg.dependsOn, ...r.addDependsOn])];
    changed.add(r.wpId);
  }

  const amendment: PlanAmendment = {
    at: ts,
    amendedBy: input.amendedBy,
    reason: input.reason,
    added: added.map((d) => d.id),
    superseded: superseded.map((s) => ({ wpId: s.wpId, replacedBy: [...s.replacedBy] })),
    rewired: rewired.map((r) => ({ wpId: r.wpId, addDependsOn: [...r.addDependsOn] })),
  };
  next.amendments = [...(next.amendments ?? []), amendment];
  const checked = WorkPlanSchema.safeParse(next);
  if (!checked.success) throw new CoordinatorError("invalid_input", "the amended plan is not valid", checked.error.issues);
  return { plan: next, amendment, changed: next.packages.filter((p) => changed.has(p.id)) };
}
